import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Database, Server, Settings as SettingsIcon, Shield } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Panel } from '@/components/erp/primitives'
import { useSession, can } from '@/shared/auth/session'
import { OverviewTab } from './OverviewTab'
import { SystemSettingsTab } from './SystemSettingsTab'
import { MySessionsPanel } from '@/features/users/MySessionsPanel'

/**
 * PORTED from pollsphere-bundle/src/components/tessera/modules/administration/index.tsx.
 * Tab-bar markup and classes are PollSphere's, unchanged.
 *
 * The tab list is now SHORTER than it was: User Management, Roles & Permissions,
 * Audit Log and Notifications each grew into a full screen with their own
 * filters, summary cards and detail canvas, and now live at their own routes.
 * Cramming a cursor-paginated audit console into a tab of a hub page was the
 * wrong shape once it had more than a table in it.
 *
 * What remains here is what is genuinely small: the hub itself, system settings,
 * and your own sessions.
 */
interface TabDef {
  id: string
  label: string
  icon: typeof Shield
  permission?: string
}

const TABS: TabDef[] = [
  { id: 'overview', label: 'Overview', icon: SettingsIcon },
  { id: 'settings', label: 'System Settings', icon: Database, permission: 'settings:read' },
  { id: 'sessions', label: 'My Sessions', icon: Server },
]

export function AdministrationModule() {
  const authorities = useSession((s) => s.authorities)
  const [params, setParams] = useSearchParams()
  const tabs = TABS.filter((t) => !t.permission || can(authorities, t.permission))
  const [fallback] = useState(tabs[0]?.id ?? 'overview')
  const activeTab = params.get('tab') && tabs.some((t) => t.id === params.get('tab'))
    ? (params.get('tab') as string)
    : fallback

  const setActiveTab = (id: string) => {
    const next = new URLSearchParams(params)
    next.set('tab', id)
    setParams(next, { replace: true })
  }

  if (tabs.length === 0) {
    return (
      <Panel title="Access Denied" description="Administration requires elevated permissions.">
        <div className="border border-red-200 bg-red-50/60 rounded-sm px-4 py-3 text-erp-sm text-red-700 flex items-center gap-2">
          <Shield className="h-4 w-4" /> You do not have permission to view this page.
        </div>
      </Panel>
    )
  }

  return (
    <div className="space-y-4">
      <div className="border-b border-border">
        <div className="flex items-center gap-1 overflow-x-auto">
          {tabs.map((t) => {
            const Icon = t.icon
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={cn(
                  'h-9 px-4 text-erp-sm border-b-2 -mb-px inline-flex items-center gap-2 whitespace-nowrap',
                  activeTab === t.id
                    ? 'border-chocolate text-chocolate font-semibold'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            )
          })}
        </div>
      </div>

      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'settings' && <SystemSettingsTab />}
      {activeTab === 'sessions' && <MySessionsPanel />}
    </div>
  )
}
